import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { useAuthStore } from '@/store/authStore'
import { FEATURES } from '@/lib/features'

import Login from '@/pages/Login'
import Dashboard from '@/pages/Dashboard'
import PatientList from '@/pages/PatientList'
import AddPatient from '@/pages/AddPatient'
import PatientDetails from '@/pages/PatientDetails'
import NewVisit from '@/pages/NewVisit'
import DrugList from '@/pages/DrugList'
import AddDrug from '@/pages/AddDrug'
import DrugDetails from '@/pages/DrugDetails'
import BillingList from '@/pages/BillingList'
import NewBill from '@/pages/NewBill'
import BillDetails from '@/pages/BillDetails'
import Settings from '@/pages/Settings'
import NotFound from '@/pages/NotFound'

function RequireAuth({ children }) {
  const user = useAuthStore((s) => s.user)
  const location = useLocation()
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }
  return children
}

function PublicOnly({ children }) {
  const user = useAuthStore((s) => s.user)
  if (user) return <Navigate to="/" replace />
  return children
}

/**
 * Feature-gated route element. If the feature is disabled, redirect to the
 * dashboard rather than showing the page that depends on missing backend
 * endpoints. This keeps the browser console silent and the UX coherent.
 */
function FeatureRoute({ enabled, children }) {
  if (!enabled) return <Navigate to="/" replace />
  return children
}

export function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicOnly>
            <Login />
          </PublicOnly>
        }
      />

      <Route
        element={
          <RequireAuth>
            <DashboardLayout />
          </RequireAuth>
        }
      >
        <Route path="/" element={<Dashboard />} />

        {/* Patients — always available */}
        <Route path="/patients" element={<PatientList />} />
        <Route path="/patients/new" element={<AddPatient />} />
        <Route path="/patients/:id" element={<PatientDetails />} />
        <Route path="/patients/:id/visits/new" element={<NewVisit />} />

        {/* Drug list / inventory (Phase 6) — gated */}
        <Route
          path="/drugs"
          element={<FeatureRoute enabled={FEATURES.pharmacy}><DrugList /></FeatureRoute>}
        />
        <Route
          path="/drugs/new"
          element={<FeatureRoute enabled={FEATURES.pharmacy}><AddDrug /></FeatureRoute>}
        />
        <Route
          path="/drugs/:id"
          element={<FeatureRoute enabled={FEATURES.pharmacy}><DrugDetails /></FeatureRoute>}
        />

        {/* Billing (Phase 7) — gated */}
        <Route
          path="/billing"
          element={<FeatureRoute enabled={FEATURES.billing}><BillingList /></FeatureRoute>}
        />
        <Route
          path="/billing/new"
          element={<FeatureRoute enabled={FEATURES.billing}><NewBill /></FeatureRoute>}
        />
        <Route
          path="/billing/:id"
          element={<FeatureRoute enabled={FEATURES.billing}><BillDetails /></FeatureRoute>}
        />

        {/* App settings — gated */}
        <Route
          path="/settings"
          element={<FeatureRoute enabled={FEATURES.settings}><Settings /></FeatureRoute>}
        />
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  )
}
